/**
 * Fee Service - Frontend utility for fetching and managing examination fees
 * 
 * This service provides a central location for managing fee data in the frontend.
 * It fetches from the backend /api/payments/fee-structure endpoint and provides
 * fallback values for offline use.
 */

import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Default fallback fees (should match backend/fees_config.py)
const DEFAULT_EXAMINATION_FEES = {
  CBEAM: { code: 'CBEAM', name: 'Aesthetic Medicine', fee: 39000, category: 'Medical Doctors' },
  CBELM: { code: 'CBELM', name: 'Longevity Medicine', fee: 35000, category: 'Medical Doctors' },
  CBERM: { code: 'CBERM', name: 'Regenerative Medicine', fee: 35000, category: 'Medical Doctors' },
  CBEAD: { code: 'CBEAD', name: 'Aesthetic Dentistry', fee: 32000, category: 'Dental' },
  CBENA: { code: 'CBENA', name: 'Aesthetic Nursing', fee: 22000, category: 'Allied' },
  CBEAA: { code: 'CBEAA', name: 'Clinical Aesthetic Assistance', fee: 22000, category: 'Allied' },
};

// Discount rates (should match backend/fees_config.py)
export const DISCOUNT_RATES = {
  INSTITUTION: 0.20,  // 20% discount for accredited institutions
  EXAM_CENTER: 0.00,  // No discount for examination center nominations
};

// Cache for fetched fees
let cachedFees = null;
let cacheExpiry = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch fee structure from backend
 * @returns {Promise<Object>} Fee structure object
 */
export async function fetchFeeStructure() {
  // Return cached if valid
  if (cachedFees && cacheExpiry && Date.now() < cacheExpiry) {
    return cachedFees;
  }

  try {
    const response = await axios.get(`${API}/api/payments/fee-structure`);
    const feeData = response.data;
    
    // Transform to our format
    const fees = {};
    if (feeData.fees && Array.isArray(feeData.fees)) {
      feeData.fees.forEach(f => {
        if (f.category === 'examination') {
          fees[f.code] = {
            code: f.code,
            name: f.name.replace(` Examination`, '').replace(`${f.code} - `, ''),
            fee: f.amount,
            category: getCategoryFromCode(f.code),
          };
        }
      });
    }
    
    // Update cache
    cachedFees = Object.keys(fees).length > 0 ? fees : DEFAULT_EXAMINATION_FEES;
    cacheExpiry = Date.now() + CACHE_DURATION;
    
    return cachedFees;
  } catch (error) {
    console.warn('Failed to fetch fee structure, using defaults:', error.message);
    return DEFAULT_EXAMINATION_FEES;
  }
}

/**
 * Get category from examination code
 */
function getCategoryFromCode(code) {
  if (['CBEAM', 'CBELM', 'CBERM'].includes(code)) return 'Medical Doctors';
  if (['CBEAD'].includes(code)) return 'Dental';
  return 'Allied';
}

/**
 * Get fee for an examination code
 * @param {string} examCode - Examination code (e.g., 'CBEAM')
 * @param {Object} feeStructure - Fee structure object (optional, uses cached/default if not provided)
 * @returns {number} Fee amount
 */
export function getExamFee(examCode, feeStructure = null) {
  const fees = feeStructure || cachedFees || DEFAULT_EXAMINATION_FEES;
  const exam = fees[examCode];
  return exam ? exam.fee : 35000;
}

/**
 * Get discounted fee for institutions (20% discount)
 * @param {string} examCode - Examination code
 * @param {Object} feeStructure - Fee structure object (optional)
 * @returns {number} Discounted fee amount
 */
export function getInstitutionDiscountedFee(examCode, feeStructure = null) {
  const baseFee = getExamFee(examCode, feeStructure);
  return Math.round(baseFee * (1 - DISCOUNT_RATES.INSTITUTION));
}

/**
 * Get fee for examination centers (no discount)
 * @param {string} examCode - Examination code
 * @param {Object} feeStructure - Fee structure object (optional)
 * @returns {number} Fee amount (same as base)
 */
export function getExamCenterFee(examCode, feeStructure = null) {
  const baseFee = getExamFee(examCode, feeStructure);
  return Math.round(baseFee * (1 - DISCOUNT_RATES.EXAM_CENTER));
}

/**
 * Get examination types for dropdowns
 * @param {Object} feeStructure - Fee structure object (optional)
 * @returns {Array} Array of examination options with value, label, fee, category
 */
export function getExaminationTypes(feeStructure = null) {
  const fees = feeStructure || cachedFees || DEFAULT_EXAMINATION_FEES;
  return Object.values(fees).map(exam => ({
    value: exam.code,
    label: `${exam.code} - ${exam.name}`,
    fee: exam.fee,
    category: exam.category,
  }));
}

/**
 * Get default examination fees (for immediate use before fetch completes)
 * @returns {Object} Default fee structure
 */
export function getDefaultFees() {
  return DEFAULT_EXAMINATION_FEES;
}

// Pre-fetch on module load
fetchFeeStructure().catch(() => {});
